import {
  forwardRef,
  useState,
  useRef,
  useLayoutEffect,
  useEffect
} from "react";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { EditorRefApi } from "shared/components/Editor/core/types/editor";
import { cx } from "shared/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";

const EDITOR_CLASSNAME = "font-bold text-base w-full max-h-[100px]";

export const TitleWithTooltip = forwardRef<
  EditorRefApi,
  {
    titleHtml?: string;
    titleText?: string;
    editable: boolean;
    onChange: (json: object, html: string) => void;
    placeholder: string;
    withViewOnly?: boolean;
    onEditModeChange?: (isEditing: boolean) => void;
  }
>(
  (
    {
      titleHtml,
      titleText,
      editable,
      onChange,
      placeholder,
      withViewOnly = true,
      onEditModeChange
    },
    ref
  ) => {
    const [isViewMode, setIsViewMode] = useState(withViewOnly);
    const [isContentTruncated, setIsContentTruncated] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<EditorRefApi>(null);
    useEffect(() => {
      if (!isViewMode && editorRef.current)
        setTimeout(() => {
          editorRef.current?.focus("end");
        }, 0);
    }, [isViewMode]);

    useLayoutEffect(() => {
      if (!isViewMode || !contentRef.current) {
        setIsContentTruncated(false);
        return;
      }

      const checkTruncation = () => {
        if (!contentRef.current) return;
        const proseMirror = contentRef.current.querySelector(
          ".ProseMirror"
        ) as HTMLElement | null;
        if (!proseMirror) {
          setIsContentTruncated(false);
          return;
        }
        const isTruncated = proseMirror.scrollHeight > proseMirror.clientHeight;
        setIsContentTruncated(isTruncated);
      };

      checkTruncation();
      const resizeObserver = new ResizeObserver(() => checkTruncation());
      resizeObserver.observe(contentRef.current);

      window.addEventListener("resize", checkTruncation);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", checkTruncation);
      };
    }, [isViewMode, titleHtml]);

    if (isViewMode && isContentTruncated)
      return (
        <Tooltip placement="bottom">
          <TooltipTrigger asChild>
            <div
              ref={contentRef}
              className={cx("cursor-pointer", EDITOR_CLASSNAME)}
              onClick={() => setIsViewMode(false)}
            >
              <LiteTextEditor
                id="task-title-view-truncated"
                editable={false}
                value={titleHtml}
                containerClassName={cx(
                  "w-full",
                  "[&_.ProseMirror]:line-clamp-2",
                  "[&_.ProseMirror]:max-h-[48px]",
                  "[&_.ProseMirror]:overflow-hidden"
                )}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent className="rounded-[11px] p-4 max-w-[488px] w-full bg-alpha-800 border z-[101] max-h-[300px] overflow-y-auto">
            <span className="text-sm text-content-1000-inverted">
              {titleText}
            </span>
          </TooltipContent>
        </Tooltip>
      );

    if (isViewMode && titleHtml)
      return (
        <div
          ref={contentRef}
          className={cx("cursor-pointer", EDITOR_CLASSNAME)}
          onClick={() => setIsViewMode(false)}
        >
          <LiteTextEditor
            id="task-title-view"
            editable={false}
            value={titleHtml}
            containerClassName={cx(
              "w-full",
              "[&_.ProseMirror]:line-clamp-2",
              "[&_.ProseMirror]:max-h-[48px]",
              "[&_.ProseMirror]:overflow-hidden"
            )}
          />
        </div>
      );

    return (
      <div ref={contentRef} className={cx("overflow-y-auto", EDITOR_CLASSNAME)}>
        <LiteTextEditor
          ref={ref}
          id="task-title"
          editable={editable}
          propertyName="title"
          value={titleHtml}
          onChange={onChange}
          placeholder={placeholder}
          onEditModeChange={(isEditing) => {
            setIsViewMode(!isEditing);
            onEditModeChange?.(isEditing);
          }}
          autoExpandConfig={{
            autoExpand: true,
            maxHeight: 300,
            allowMultiline: true,
            preventNewlines: false
          }}
          containerClassName={cx(
            "w-full min-h-[24px]",
            "[&_.ProseMirror]:break-words",
            "[&_.ProseMirror]:overflow-wrap-anywhere",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:font-bold",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-base",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-content-100",
            "[&_.ProseMirror_p.is-editor-empty:first-child::before]:opacity-100"
          )}
        />
      </div>
    );
  }
);

TitleWithTooltip.displayName = "TitleWithTooltip";
